<template>
	<div class="grid-flex-container" v-if="initialized && workbook">
		<div class="grid-toolbar">
			<span class="view-title">{{ workbook.Name }}</span>
			<v-row class="quick-filter-container" no-gutters></v-row>
			<!-- Export Modal -->
			<export-modal
				:timezone-options="true"
				:execution-options="false"
				:data-scope-options="true"
				@exportModalEvent="onExportModalEvent"
			>
				<template #activator="{ on, attrs }">
					<v-btn
						v-bind="attrs"
						v-on="on"
						class="export"
						color="primary"
					>
						<v-icon class="mr-1">mdi-cloud-download</v-icon>
						Export
					</v-btn>
				</template>
			</export-modal>
		</div>
		<v-tabs
			class="px-2"
			v-model="tab"
			center-active
			show-arrows
			style="flex: none"
		>
			<v-tab
				v-for="(report, index) in workbookreports"
				:key="index"
				@change="setReport(report)"
				>{{ report.TabName }}</v-tab
			>
		</v-tabs>
		<div class="generic-grid-container">
			<WorkbookReportPreview
				:hasFilters="selectedReportHasFilters"
				:reportName="selectedReportName"
				:visibleColumns="selectedVisibleColumns"
				:filterColumns="getFilterColumns()"
				:gridData="selectedGridData"
				:irisUserMetadata="selectedIrisUserMetadata"
				:loadingData="selectedLoadingData"
				:page="selectedPage"
				:rowsPerPage="selectedRowsPerPage"
				:totalCount="selectedTotalCount"
				:quickFilter="selectedQuickFilter"
				:advancedFilter="selectedAdvancedFilter"
				:advancedFiltersCount="selectedAdvancedFiltersCount"
				:sorting="selectedSorting"
				@pageChanged="onPageChanged"
				@sortChanged="onSortChanged"
				@rowsPerPageChanged="onRowsPerPageChanged"
				@quickFilterChanged="onQuickFilterChanged"
				@advancedFilterChanged="onAdvancedFilterChanged"
				v-if="tabChanged && selectedWorkbookReport"
			/>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { ReportWorkbookGetters } from '../../../store/getters';
import { ReportWorkbookActions } from '../../../store/actions';
import ExportModal from '../../shared/export/ExportModal.vue';

import WorkbookReportPreview from './WorkbookReportPreview.vue';
import { nextTick } from 'vue';
import { reportTypes } from '../../../enums/reportTypes';

export default {
	name: 'Workbook',
	components: { WorkbookReportPreview, ExportModal },
	computed: {
		...mapGetters({
			workbook: [ReportWorkbookGetters.SELECTED_WORKBOOK],
		}),

		isSingleFilterWorkbook() {
			if (!this.workbook) return false;
			return this.workbook.IsSingleFilter;
		},
		workbookreports() {
			if (!this.workbook) return [];
			if (!this.workbook.WorkbookReports) return [];
			let wr = JSON.parse(JSON.stringify(this.workbook.WorkbookReports));
			return wr.sort((a, b) => (a.Order > b.Order ? 1 : -1));
		},

		currentWorkbookId() {
			return this.$route.params.workbookId;
		},

		selectedGridView() {
			if (!this.selectedWorkbookReport) return null;
			if (!this.selectedWorkbookReport.Report) return null;
			if (!this.selectedWorkbookReport.Report.GridView) return null;
			return this.selectedWorkbookReport.Report.GridView;
		},
		selectedVisibleColumns() {
			if (!this.selectedPayloadItem) return [];
			return this.selectedPayloadItem.columns;
		},

		selectedPayloadItem() {
			if (!this.selectedWorkbookReport) return null;
			if (this.payload == null) return null;
			if (this.payload.length < 1) return null;
			let payloadItem = this.payload.find(
				(x) => x.ReportId === this.selectedWorkbookReport.ReportId
			);
			if (!payloadItem) null;
			return payloadItem;
		},
		selectedReportName() {
			if (!this.selectedWorkbookReport) return null;
			if (!this.selectedWorkbookReport.Report) return null;
			return this.selectedWorkbookReport.Report.Name;
		},
		selectedReportHasFilters() {
			if (!this.selectedWorkbookReport) return null;
			if (!this.selectedWorkbookReport.Report) return null;
			return (
				this.selectedWorkbookReport.Report.Type == reportTypes.RAW_DATA
			);
		},

		selectedGridData() {
			if (!this.selectedPayloadItem) return [];
			return this.selectedPayloadItem.gridData;
		},
		selectedIrisUserMetadata() {
			if (!this.selectedPayloadItem) return [];
			return this.selectedPayloadItem.irisUsersMetadata;
		},
		selectedLoadingData() {
			if (!this.selectedPayloadItem) return false;
			return this.selectedPayloadItem.loading;
		},
		selectedPage() {
			if (!this.selectedPayloadItem) return 1;
			return this.selectedPayloadItem.page;
		},
		selectedRowsPerPage() {
			if (!this.selectedPayloadItem) return 50;
			return this.selectedPayloadItem.rowsPerPage;
		},
		selectedTotalCount() {
			if (!this.selectedPayloadItem) return 0;
			return this.selectedPayloadItem.totalCount;
		},
		selectedQuickFilter() {
			if (this.isSingleFilterWorkbook) {
				return this.singleFilterWorkbookQuickFilter;
			} else {
				if (!this.selectedPayloadItem)
					return { FieldId: null, Value: '' };
				return this.selectedPayloadItem.quickFilter;
			}
		},
		selectedAdvancedFilter() {
			if (this.isSingleFilterWorkbook) {
				return this.singleFilterWorkbookAdvancedFilter;
			} else {
				if (!this.selectedPayloadItem)
					return { Operator: 1, Filters: [], FiltersCount: 0 };
				return this.selectedPayloadItem.advancedFilter;
			}
		},
		selectedAdvancedFiltersCount() {
			if (this.isSingleFilterWorkbook) {
				return this.singleFilterWorkbookAdvancedFilter.Filters.length;
			} else {
				return this.selectedAdvancedFilter.Filters.length;
			}
		},
		selectedSorting() {
			if (!this.selectedPayloadItem) return [];
			return this.selectedPayloadItem.sorting;
		},
	},
	data() {
		return {
			initialized: false,
			tab: 0,
			payload: [],
			singleFilterWorkbookQuickFilter: { FieldId: null, Value: '' },
			singleFilterWorkbookAdvancedFilter: {
				Operator: 1,
				Filters: [],
				FiltersCount: 0,
			},
			selectedWorkbookReport: null,
			tabChanged: false,
		};
	},
	mounted() {
		if (this.currentWorkbookId) {
			this.$store
				.dispatch(
					ReportWorkbookActions.LOAD_WORKBOOK,
					this.currentWorkbookId
				)
				.then((success) => {
					if (!success) return;
					if (!this.workbook) return;
					if (!this.workbook.WorkbookReports) return;
					if (this.workbookreports && this.workbookreports.length > 0)
						this.workbookreports.forEach((wr) => {
							let hasRawData =
								wr.Report.Type == reportTypes.RAW_DATA;

							let item = {
								hasRawData: hasRawData,
								ReportId: wr.ReportId,
								gridView: hasRawData
									? this.getReportGridView(wr)
									: null,
								quickFilter: { FieldId: null, Value: '' },
								advancedFilter: {
									Operator: 1,
									Filters: [],
									FiltersCount: 0,
								},
								sorting: [],
								rowsPerPage: 50,
								page: 1,
								columns: this.getWorkbookReportColumns(
									wr,
									hasRawData
								),
								loading: false,
								gridData: [],
								irisUsersMetadata: [],
								totalCount: 0,
							};
							this.payload.push(item);
						});

					this.initialized = true;

					this.setReport(this.workbookreports[0]);
				});
		}
	},
	destroyed() {
		this.payload = [];
		this.$store.dispatch(ReportWorkbookActions.CLEAR);
	},
	methods: {
		async setReport(item) {
			this.tabChanged = false;
			this.selectedWorkbookReport = item;
			await nextTick();
			this.tabChanged = true;
			this.loadData();
		},
		getWorkbookReportColumns(wr, hasRawData) {
			if (!wr) return [];
			if (!wr.Report) return [];
			if (!wr.Report.Fields) return [];
			if (hasRawData) {
				if (!wr.Report.GridView) return [];
				if (!wr.Report.GridView.Columns) return [];
				let visibleColumns = wr.Report.GridView.Columns.filter(
					(x) => x.IsVisible === true
				).sort((a, b) => a.Order - b.Order);
				let reportFields = wr.Report.Fields.sort(
					(a, b) => a.Order - b.Order
				);
				let cols = [];
				reportFields.forEach((rf) => {
					let reportField = visibleColumns.find(
						(x) => x.Id == rf.GridViewFieldId
					);
					if (reportField) {
						reportField.Label = rf.Label;
						cols.push(reportField);
					}
				});
				return cols;
			} else {
				let columns = wr.Report.Fields.map((x) => ({
					Id: x.Id,
					FieldName: x.Source,
					Header: x.Label,
					Order: x.Order,
				}));
				return columns.sort((a, b) => a.Order - b.Order);
			}
		},
		getReportGridView(wr) {
			if (!wr) return [];
			if (!wr.Report) return [];
			if (!wr.Report.Fields) return [];
			if (!wr.Report.GridView) return [];
			return wr.Report.GridView;
		},

		onQuickFilterChanged(e) {
			if (this.isSingleFilterWorkbook) {
				this.singleFilterWorkbookQuickFilter = e;
			} else {
				let payloadItem = this.payload.find(
					(x) => x.ReportId === this.selectedWorkbookReport.ReportId
				);
				payloadItem.quickFilter = e;
			}
			this.loadData();
		},
		onAdvancedFilterChanged(e) {
			if (this.isSingleFilterWorkbook) {
				this.singleFilterWorkbookAdvancedFilter = e;
			} else {
				let payloadItem = this.payload.find(
					(x) => x.ReportId === this.selectedWorkbookReport.ReportId
				);
				payloadItem.advancedFilter = e;
			}

			this.loadData();
		},
		onRowsPerPageChanged(e) {
			let payloadItem = this.payload.find(
				(x) => x.ReportId === this.selectedWorkbookReport.ReportId
			);
			payloadItem.rowsPerPage = e;
			this.loadData();
		},
		onPageChanged(e) {
			let payloadItem = this.payload.find(
				(x) => x.ReportId === this.selectedWorkbookReport.ReportId
			);
			payloadItem.page = e;
			this.loadData();
		},
		onSortChanged(e) {
			let payloadItem = this.payload.find(
				(x) => x.ReportId === this.selectedWorkbookReport.ReportId
			);
			payloadItem.sorting = e;
			this.loadData();
		},

		loadData() {
			let payloadItem = this.payload.find(
				(x) => x.ReportId === this.selectedWorkbookReport.ReportId
			);
			payloadItem.loading = true;

			let requestpayload = {};
			if (payloadItem.hasRawData) {
				requestpayload.ReportId = this.selectedWorkbookReport.ReportId;
				requestpayload.ViewId = this.selectedGridView.Id;
				requestpayload.Columns = this.selectedVisibleColumns.map(
					(x) => x.Id
				);
				requestpayload.IncludeCount = true;
				requestpayload.Page = this.selectedPage;
				requestpayload.RowsPerPage = this.selectedRowsPerPage;

				if (this.selectedSorting.length > 0)
					requestpayload.Sorting = this.selectedSorting;
				if (this.isSingleFilterWorkbook) {
					if (
						this.singleFilterWorkbookAdvancedFilter.Filters.length >
						0
					)
						requestpayload.AdvancedFilter =
							this.singleFilterWorkbookAdvancedFilter;
					if (this.singleFilterWorkbookQuickFilter.Value)
						requestpayload.QuickFilter =
							this.singleFilterWorkbookQuickFilter;
				} else {
					if (this.selectedAdvancedFilter.Filters.length > 0)
						requestpayload.AdvancedFilter =
							this.selectedAdvancedFilter;
					if (this.selectedQuickFilter.Value)
						requestpayload.QuickFilter = this.selectedQuickFilter;
				}
			} else {
				requestpayload.ReportId = this.selectedWorkbookReport.ReportId;
				requestpayload.IncludeCount = true;
				requestpayload.Page = this.selectedPage;
				requestpayload.RowsPerPage = this.selectedRowsPerPage;
			}

			this.$store
				.dispatch(
					ReportWorkbookActions.LOAD_REPORT_DATA,
					requestpayload
				)
				.then((resp) => {
					payloadItem.totalCount = resp.Count;
					payloadItem.gridData = resp.Data;
					payloadItem.irisUserMetadata = resp.IrisUsers;
				})
				.finally(() => {
					payloadItem.loading = false;
				});
		},

		getReportPreviews(allData, timezoneOffset) {
			let result = [];
			this.payload.forEach((payloadItem) => {
				let requestpayload = {
					ReportId: payloadItem.ReportId,
					ViewId: payloadItem.hasRawData
						? payloadItem.gridView.Id
						: payloadItem.ReportId,
					Columns: payloadItem.columns.map((c) => c.Id),
					IncludeCount: true,
					Page: allData === true ? 1 : payloadItem.page,
					RowsPerPage:
						allData === true
							? payloadItem.totalCount
							: payloadItem.rowsPerPage,
					TimeZone: timezoneOffset,
				};

				if (payloadItem.sorting.length > 0)
					requestpayload.Sorting = payloadItem.sorting;
				if (this.isSingleFilterWorkbook) {
					if (
						this.singleFilterWorkbookAdvancedFilter.Filters.length >
						0
					)
						requestpayload.AdvancedFilter =
							this.singleFilterWorkbookAdvancedFilter;
					if (this.singleFilterWorkbookQuickFilter.Value)
						requestpayload.QuickFilter =
							this.singleFilterWorkbookQuickFilter;
				} else {
					if (payloadItem.advancedFilter.Filters.length > 0)
						requestpayload.AdvancedFilter =
							payloadItem.advancedFilter;
					if (payloadItem.quickFilter.Value)
						requestpayload.QuickFilter = payloadItem.quickFilter;
				}

				result.push(requestpayload);
			});

			return result;
		},

		export(allData, timezoneOffset) {
			let payload = {
				payload: {
					WorkbookId: this.currentWorkbookId,
					ReportsPreview: this.getReportPreviews(
						allData,
						timezoneOffset
					),
				},
				workbookName: this.workbook.Name,
			};

			this.$store.dispatch(ReportWorkbookActions.EXPORT, payload);
		},
		getFilterColumns() {
			if (this.isSingleFilterWorkbook) {
				return this.getSingleFilterWorkbookColumns();
			} else {
				return this.selectedVisibleColumns;
			}
		},
		getSingleFilterWorkbookColumns() {
			if (!this.workbook) return [];
			const columnsPerWorkbook = this.payload.map(
				(wb) => wb.columns || []
			);
			const setsOfFieldNames = columnsPerWorkbook.map(
				(cols) => new Set(cols.map((c) => c.FieldName))
			);
			const commonFieldNames = setsOfFieldNames.reduce(
				(acc, currentSet) => {
					return new Set(
						[...acc].filter((fieldName) =>
							currentSet.has(fieldName)
						)
					);
				}
			);

			const firstWorkbookColumns = columnsPerWorkbook[0];
			const commonColumns = firstWorkbookColumns.filter((col) =>
				commonFieldNames.has(col.FieldName)
			);

			let result = commonColumns.sort((a, b) => {
				const labelA = a.Label || '';
				const labelB = b.Label || '';
				return labelA.localeCompare(labelB);
			});
			return result;
		},
		onExportModalEvent(exportConfig) {
			const timezone =
				exportConfig.timezone === 'utc'
					? 0
					: new Date().getTimezoneOffset() * -1;
			this.export(exportConfig.allData, timezone);
		},
	},
};
</script>
